import { Component } from 'react';
import styled from 'styled-components';
import { FormItemLabel, ISelectOption, Icon, Input, Select, ValidationState } from '..';
import { Paragraph } from '../Paragraph/Paragraph';

const WrapperElement = styled.div<{ disableMarginBottom?: boolean }>`
  margin: ${props => (!props.disableMarginBottom ? props.theme.vars.form.item.margin : 0)};
`;

const ContainerElement = styled.div`
  display: inline-block;
  width: 100%;
`;

const DurationPickerElement = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;

const DurationInputElement = styled.div`
  width: 80px;
  margin-right: 10px;
  input {
    text-align: center;
  }
`;

const DurationSelectWrapper = styled.div`
  max-width: 155px;
  width: 100%;
`;

const ValidationMessage = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  .validation-message {
    color: ${props => props.theme.vars.newColor.orange2};
    font-weight: 400;
    margin: 0;
  }
  svg {
    color: ${props => props.theme.vars.newColor.orange2};
    margin-right: 10px;
  }
`;

export interface IDurationPickerProps {
  /** Duration label */
  label?: string;
  /** A hint can be passed as HTML to explain what the form element is used for */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** The numeric value representing the duration time. Defaults to 1 */
  value?: number;
  /** The minumum allowed value for the duration time. Defaults to undefined. */
  min?: number;
  /** The maximum allowed value for the duration time. Defaults to undefined. */
  max?: number;
  /** The unit.  "minutes" (default) | "hours" */
  unit?: string;
  /** The units to display in the select.  Defaults to Minutes & Hours */
  units?: Array<ISelectOption<string>>;
  /** Specifies the name of the input element */
  inputName?: string;
  /** Event that fires whenever the value or the unit changes. */
  onChange?: (value: number, unit: string) => void;
  /** Whether the units are ready-only or not.  Defaults to false */
  readOnlyUnits?: boolean;
  /** The tab index of the duration picker input.  Defaults to 0. */
  tabIndex?: number;
  /** Input validation state (complete, warning)  */
  validationState?: ValidationState;
  /** Input validation message  */
  validationMessage?: string;
  /** Disable margin bottom */
  disableMarginBottom?: boolean;
  ariaLabel: string;
  unitAriaLabel: string;
  unitPlaceholder?: string;
  disabled?: boolean;
}

export class DurationPicker extends Component<IDurationPickerProps> {
  public static defaultProps: Pick<IDurationPickerProps, 'value' | 'unit'> = {
    value: 1,
    unit: 'minutes',
  };

  public render() {
    const durationUnits: Array<ISelectOption<string>> = this.props.units ? this.props.units : units;

    return (
      <WrapperElement disableMarginBottom={this.props.disableMarginBottom}>
        <ContainerElement>
          <FormItemLabel
            name={this.props.label}
            hint={this.props.hint}
            tooltipHeading={this.props.tooltipHeading}
            tooltipDescription={this.props.tooltipDescription}
            tooltipWidth={this.props.tooltipWidth}
            disabled={this.props.disabled}
          />
          <DurationPickerElement>
            <DurationInputElement>
              <Input
                type="number"
                value={this.props.value.toString()}
                name={this.props.inputName}
                onChange={this.handleInputChanged}
                tabIndex={this.props.tabIndex || 0}
                inputTestId="DurationInput"
                spacingBelow={false}
                validationState={this.props.validationState}
                ariaLabel={this.props.ariaLabel}
                disabled={this.props.disabled}
                min={this.props.min?.toString()}
                max={this.props.max?.toString()}
              />
            </DurationInputElement>
            {this.props.readOnlyUnits && <Paragraph>{this.props.unit}</Paragraph>}
            {!this.props.readOnlyUnits && (
              <DurationSelectWrapper>
                <Select
                  ariaLabel={this.props.unitAriaLabel}
                  placeholder={this.props.unitPlaceholder}
                  inline={true}
                  options={durationUnits}
                  onChange={this.handleDropdownChanged}
                  spacingBelow={false}
                  validationState={this.props.validationState}
                  disabled={this.props.disabled}
                  value={this.props.unit ? durationUnits.find(unit => unit.value === this.props.unit) : null}
                />
              </DurationSelectWrapper>
            )}
          </DurationPickerElement>
          {this.props.validationMessage && (
            <ValidationMessage>
              <Icon icon="exclamation-triangle" size="small" />
              <p className="validation-message">{this.props.validationMessage}</p>
            </ValidationMessage>
          )}
        </ContainerElement>
      </WrapperElement>
    );
  }

  private handleInputChanged = value => {
    if (this.props.onChange) {
      this.props.onChange(value, this.props.unit);
    }
  };

  private handleDropdownChanged = (option: ISelectOption<string>) => {
    if (this.props.onChange) {
      this.props.onChange(this.props.value, option.value);
    }
  };
}

const units: Array<ISelectOption<string>> = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
];
